import { MODULE_ACTION_BITS, MODULE_IDS } from "./manifest"

/** Super Admin: full access, sent as "*" rather than an expanded set. */
export function isFullAccess(grants) {
  return grants === "*"
}

/**
 * `[{ module_id, mask }]` from login → `Map<moduleKey, bitmask>`.
 */
export function grantsToMap(grants) {
  const map = new Map()
  if (!Array.isArray(grants)) return map
  for (const row of grants) {
    const key = MODULE_IDS[Number(row.module_id)]
    if (!key) continue
    // OR rather than set: a module could in principle arrive more than once, and
    // overwriting would silently drop the earlier grant.
    map.set(key, (map.get(key) ?? 0) | (Number(row.mask) || 0))
  }
  return map
}

export function hasAssignedRole(user) {
  const id = user?.role_id
  return id != null && Number.isFinite(Number(id)) && Number(id) > 0
}

/**
 * May this user do `module:action`?
 *
 * A user with no role is granted NOTHING. The tempting default is `true` — "we
 * don't know, let them try" — but the server answers 403 NO_ROLE_ASSIGNED for
 * exactly these accounts, so a permissive default renders a whole app of controls
 * that fail on click and hides the real problem, which is that somebody needs to
 * assign them a role.
 */
export function can(user, moduleKey, action) {
  if (!user) return false
  if (isFullAccess(user.grants)) return true
  if (!hasAssignedRole(user)) return false

  const bit = MODULE_ACTION_BITS[moduleKey]?.[action]
  if (!bit) return false

  const mask = grantsToMap(user.grants).get(moduleKey) ?? 0
  return (mask & bit) === bit
}

/** The action names a user holds on a module — for showing what a role can do. */
export function actionsFor(user, moduleKey) {
  const bits = MODULE_ACTION_BITS[moduleKey]
  if (!bits) return []
  if (isFullAccess(user?.grants)) return Object.keys(bits)
  const mask = grantsToMap(user?.grants).get(moduleKey) ?? 0
  return Object.entries(bits)
    .filter(([, bit]) => (mask & bit) === bit)
    .map(([action]) => action)
}
